import React, { Fragment, useState, useEffect } from 'react';
import { Modal, ButtonToolbar } from 'reactstrap';
import Button from '@material-ui/core/Button';
import moment from 'moment';
import GavelIcon from 'mdi-react/GavelIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import FileDocumentOutlineIcon from 'mdi-react/FileDocumentOutlineIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import {
	_capitalizeText,
	_replaceAbbreviations,
	_formatNumber,
	_normalizeLegalPartyName,
	_extractIndividualNames,
	_getFirstAndLastFromFullName,
} from '../../../shared/helpers/utils';
import { _axiosCall } from '../../../shared/helpers/apicalls';
import SkipTraceUnifiedModal from '../../../shared/components/skiptrace/SkipTraceUnifiedModal';

const ACRIS_DOC_LABELS = {
	fl: 'Foreclosure Lien',
	ftl: 'Federal Tax Lien',
	tls: 'Tax Lien',
	ntxl: 'Non-Tax Lien',
	dpftl: 'Property Tax Lien',
	judg: 'Judgment',
	locc: 'Common Lien',
	tolcc: 'Mechanics Lien',
	brup: 'Bankruptcy',
};

const _formatStageLabel = (stage) => {
	if (!stage) return '';
	return stage
		.toString()
		.split('_')
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
};

const _formatDate = (val) => {
	if (!val) return '--';
	const m = moment(val);
	if (!m.isValid() || m.year() < 1971) return '--';
	return m.format('MMMM D, YYYY');
};

const _formatDateTime = (val) => {
	if (!val) return '--';
	const m = moment(val);
	if (!m.isValid() || m.year() < 1971) return '--';
	return m.format('MMMM D, YYYY h:mm A');
};

const _parseDocs = (raw) => {
	if (!raw) return [];
	if (Array.isArray(raw)) return raw;
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch (e) {
		return [];
	}
};

const labelStyle = {
	fontSize: 11,
	color: '#858AA1',
	textTransform: 'uppercase',
	letterSpacing: '0.04em',
	marginBottom: 4,
	fontWeight: 600,
};
const valueStyle = { fontSize: 13, color: '#23282C', fontWeight: 500, wordBreak: 'break-word' };
const sectionStyle = { padding: '16px 0', borderTop: '1px solid #EEF0F5' };

const ForeclosureDetailModal = ({ isOpen, onClose, foreclosureCase, property, liens, allCases, user }) => {
	const [openModalKey, setOpenModalKey] = useState(null);
	const [sigState, setSigState] = useState({ checked: false, hasSignatory: false });

	const fc = foreclosureCase;

	// Debtor/Defendant skip-trace prep. Persons (and recoverable fiduciaries)
	// get a person lookup; pure entities are gated on having an ACRIS signatory
	// on file for this property (same source as the owner-side LLC unmask).
	const debtorNormalized = fc ? _normalizeLegalPartyName(fc.debtor) : '';
	const debtorPersons = debtorNormalized ? _extractIndividualNames(debtorNormalized) : [];
	const debtorIsEntity = !!debtorNormalized && debtorPersons.length === 0;
	const propertyKey = property && property.key;

	useEffect(() => {
		if (!isOpen || !debtorIsEntity || !propertyKey) return undefined;

		let cancelled = false;
		setSigState({ checked: false, hasSignatory: false });

		const allKeys = [propertyKey];
		if (Array.isArray(property.alternate_keys)) {
			property.alternate_keys.forEach(ak => allKeys.push(ak));
		}

		_axiosCall({
			url: process.env.API_ROOT + '/query/acris-signatories?cache=60',
			method: 'post',
			query: {
				q: '*:*',
				fq: ['{!terms f=property}' + allKeys.join(',')],
				fl: 'property,signatory_name',
				rows: 1,
				wt: 'json',
			},
		}).then(res => {
			if (cancelled) return;
			const has = !!(res && res.docs && res.docs.length > 0 && res.docs[0].signatory_name);
			setSigState({ checked: true, hasSignatory: has });
		}).catch(() => {
			if (!cancelled) setSigState({ checked: true, hasSignatory: false });
		});

		return () => { cancelled = true; };
	}, [isOpen, debtorIsEntity, propertyKey]); // eslint-disable-line react-hooks/exhaustive-deps

	if (!isOpen) return null;
	const docs = fc ? _parseDocs(fc.case_documents) : [];
	const stages = fc && Array.isArray(fc.case_stages)
		? fc.case_stages
		: (fc && fc.case_stages ? [fc.case_stages] : []);
	const hasAuction = fc && fc.has_auction;
	const isSold = fc && fc.auction_status === 'sold';
	const otherCases = Array.isArray(allCases) && fc
		? allCases.filter(c => c.id !== fc.id)
		: [];

	const propertyAddress = property
		? `${_capitalizeText(_replaceAbbreviations(property.display_full_street_address || property.full_street_address || ''))}${property.address_2_normalized ? ', ' + property.address_2_normalized.toUpperCase().replace('APT', 'Apt') : ''}`
		: '';
	const cityLine = property
		? `${property.neighborhood && property.neighborhood[0] ? _capitalizeText(property.neighborhood[0]) + ', ' : ''}${_capitalizeText(property.borough || '')} ${property.zip || ''}`
		: '';

	// Shared address context for any skip-trace lookup launched from this case
	// (the debtor is tied to this property).
	const skipTraceAddressProps = {
		address_1: '' + (property && property.display_full_street_address ? property.display_full_street_address : ''),
		address_2: '' + (property && property.address_2_normalized ? property.address_2_normalized : ''),
		city: '' + (property && property.city ? property.city : ''),
		county: '' + (property && property.county ? property.county : ''),
		state: '' + (property && property.state ? property.state : ''),
		zip: '' + (property && property.zip ? property.zip : ''),
		borough_code: property ? property.borough_code : undefined,
		block: property ? property.block : undefined,
		lot: property ? property.lot : undefined,
		property: property,
	};

	const _renderDebtorParty = () => {
		if (!fc || !fc.debtor) return <div style={valueStyle}>--</div>;

		// People (and recovered fiduciaries) → person skip-trace, always shown.
		if (debtorPersons.length > 0) {
			return (
				<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
					{debtorPersons.map((name, idx) => {
						const person = _getFirstAndLastFromFullName(name);
						const displayName = person.display
							? person.display
							: [person.first_name, person.middle_name, person.last_name].filter(Boolean).join(' ');
						const modalKey = 'debtor-' + idx;
						return (
							<SkipTraceUnifiedModal
								{...skipTraceAddressProps}
								user={user}
								key={'stm-debtor-' + idx}
								first_name={'' + person.first_name}
								last_name={'' + person.last_name}
								triggerDisplay={_capitalizeText(displayName)}
								isOpen={openModalKey === modalKey}
								onOpen={() => setOpenModalKey(modalKey)}
								onClose={() => setOpenModalKey(null)}
							/>
						);
					})}
				</div>
			);
		}

		// Entity / LLC → skip-trace (signatory) trigger ONLY when a signatory is
		// on file for this property; otherwise plain text.
		if (debtorIsEntity && sigState.checked && sigState.hasSignatory) {
			const modalKey = 'debtor-llc';
			return (
				<SkipTraceUnifiedModal
					{...skipTraceAddressProps}
					user={user}
					first_name={''}
					last_name={''}
					business_name={debtorNormalized}
					triggerDisplay={_capitalizeText(debtorNormalized)}
					isOpen={openModalKey === modalKey}
					onOpen={() => setOpenModalKey(modalKey)}
					onClose={() => setOpenModalKey(null)}
				/>
			);
		}

		// Estates/heirs (suppressed), unparseable, or entity with no signatory
		// on file → plain text.
		return <div style={valueStyle}>{_capitalizeText(fc.debtor)}</div>;
	};

	return (
		<Modal
			isOpen={isOpen}
			modalClassName="ltr-support"
			className="modal-dialog--primary modal-dialog--header"
			toggle={onClose}
			style={{ minWidth: 720 }}
			scrollable
		>
			<div className="modal__header">
				<button className="lnr lnr-cross modal__close-btn" onClick={onClose} />
				<h4 className="modal__title">
					<GavelIcon size={22} style={{ marginRight: 8, marginTop: -2 }} />
					Foreclosure Detail{fc && fc.index_number ? ' — ' + fc.index_number : ''}
				</h4>
			</div>

			<div className="modal__body" style={{ textAlign: 'left' }}>
				{propertyAddress && (
					<div style={{ paddingBottom: 16 }}>
						<div style={labelStyle}>Property</div>
						<div style={{ ...valueStyle, fontSize: 15 }}>{propertyAddress}</div>
						{cityLine.trim() && (
							<div style={{ fontSize: 12, color: '#858AA1', marginTop: 2 }}>{cityLine}</div>
						)}
					</div>
				)}

				{!fc && (
					<div style={{ ...sectionStyle, color: '#858AA1', fontSize: 13 }}>
						No NYSCEF foreclosure case data for this property. See ACRIS distress liens below.
					</div>
				)}

				{fc && (
					<Fragment>
						<div style={sectionStyle}>
							<div style={labelStyle}>Caption</div>
							<div style={valueStyle}>{fc.caption ? _capitalizeText(fc.caption) : '--'}</div>
						</div>

						<div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
							<div>
								<div style={labelStyle}>Creditor / Plaintiff</div>
								<div style={valueStyle}>{fc.creditor ? _capitalizeText(fc.creditor) : '--'}</div>
							</div>
							<div>
								<div style={labelStyle}>Debtor / Defendant</div>
								{_renderDebtorParty()}
							</div>
						</div>

						<div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
							<div>
								<div style={labelStyle}>Original Amount</div>
								<div style={valueStyle}>{fc.original_amount ? '$' + _formatNumber(fc.original_amount) : '--'}</div>
							</div>
							<div>
								<div style={labelStyle}>Court</div>
								<div style={valueStyle}>{fc.court ? _capitalizeText(fc.court) : '--'}</div>
							</div>
							<div>
								<div style={labelStyle}>Case Type</div>
								<div style={valueStyle}>{fc.case_type ? _capitalizeText(fc.case_type) : '--'}</div>
							</div>
						</div>

						<div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
							<div>
								<div style={labelStyle}>Filing Date</div>
								<div style={valueStyle}>{_formatDate(fc.filing_date)}</div>
							</div>
							<div>
								<div style={labelStyle}>Lis Pendens Date</div>
								<div style={valueStyle}>{_formatDate(fc.lis_pendens_date)}</div>
							</div>
						</div>

						{stages.length > 0 && (
							<div style={sectionStyle}>
								<div style={labelStyle}>Case Stage Progression</div>
								<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 8 }}>
									{stages.map((stage, idx) => {
										const isCurrent = fc.case_stage === stage;
										const isLast = idx === stages.length - 1;
										return (
											<Fragment key={idx}>
												<span
													style={{
														padding: '4px 12px',
														borderRadius: 15,
														fontSize: 11,
														fontWeight: 600,
														background: isCurrent ? '#780F9E' : 'rgba(120,15,158,0.10)',
														color: isCurrent ? '#FFF' : '#780F9E',
														border: '1px solid rgba(120,15,158,0.40)',
														whiteSpace: 'nowrap',
													}}
												>
													{_formatStageLabel(stage)}
												</span>
												{!isLast && <ChevronRightIcon size={16} color="#A3ADC2" style={{ flexShrink: 0 }} />}
											</Fragment>
										);
									})}
								</div>
							</div>
						)}

						{hasAuction && (
							<div style={sectionStyle}>
								<div style={labelStyle}>Auction</div>
								<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
									<div>
										<div style={labelStyle}>Sale Date</div>
										<div style={valueStyle}>{_formatDateTime(fc.auction_sale_datetime || fc.auction_sale_date)}</div>
									</div>
									<div>
										<div style={labelStyle}>Status</div>
										<div style={valueStyle}>{fc.auction_status ? _capitalizeText(fc.auction_status) : '--'}</div>
									</div>
									<div>
										<div style={labelStyle}>Upset Price</div>
										<div style={valueStyle}>{fc.auction_upset_price ? '$' + _formatNumber(fc.auction_upset_price) : '--'}</div>
									</div>
									<div>
										<div style={labelStyle}>Postponements</div>
										<div style={valueStyle}>{fc.auction_postponement_count || 0}</div>
									</div>
									<div style={{ gridColumn: 'span 2' }}>
										<div style={labelStyle}>Location</div>
										<div style={valueStyle}>{fc.auction_location || '--'}</div>
									</div>
									{fc.auction_referee && (
										<div style={{ gridColumn: 'span 2' }}>
											<div style={labelStyle}>Referee</div>
											<div style={valueStyle}>{fc.auction_referee}</div>
										</div>
									)}
								</div>
							</div>
						)}

						{isSold && (
							<div style={sectionStyle}>
								<div style={labelStyle}>Sale Outcome</div>
								<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 4 }}>
									<div>
										<div style={labelStyle}>Sold Date</div>
										<div style={valueStyle}>{_formatDate(fc.sold_date)}</div>
									</div>
									<div>
										<div style={labelStyle}>Sold Price</div>
										<div style={valueStyle}>{fc.sold_price ? '$' + _formatNumber(fc.sold_price) : '--'}</div>
									</div>
									<div>
										<div style={labelStyle}>Buyer</div>
										<div style={valueStyle}>{fc.sold_buyer ? _capitalizeText(fc.sold_buyer) : '--'}</div>
									</div>
								</div>
							</div>
						)}

						{docs.length > 0 && (
							<div style={sectionStyle}>
								<div style={labelStyle}>Case Documents ({docs.length})</div>
								<div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', border: '1px solid #EEF0F5', borderRadius: 6 }}>
									{docs.map((doc, idx) => (
										<div
											key={idx}
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												padding: '10px 14px',
												borderBottom: idx < docs.length - 1 ? '1px solid #EEF0F5' : 'none',
												fontSize: 12,
											}}
										>
											<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
												<FileDocumentOutlineIcon size={16} color={'#858AA1'} />
												<div>
													<div style={{ fontWeight: 600, color: '#23282C' }}>
														Doc #{doc.doc_number}{doc.title ? ' — ' + _capitalizeText(doc.title) : ''}
													</div>
													{doc.filed_date && (
														<div style={{ color: '#858AA1', fontSize: 11, marginTop: 2 }}>Filed {doc.filed_date}</div>
													)}
												</div>
											</div>
											{doc.nyscef_url && (
												<a
													href={doc.nyscef_url}
													target="_blank"
													rel="noopener noreferrer"
													style={{ color: '#780F9E', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500 }}
												>
													View <OpenInNewIcon size={14} />
												</a>
											)}
										</div>
									))}
								</div>
							</div>
						)}
					</Fragment>
				)}

				{otherCases.length > 0 && (
					<div style={sectionStyle}>
						<div style={labelStyle}>Other Cases on This Property ({otherCases.length})</div>
						<div style={{ marginTop: 8, maxHeight: 280, overflowY: 'auto', border: '1px solid #EEF0F5', borderRadius: 6 }}>
							{otherCases.map((c, idx) => (
								<div key={c.id || idx} style={{ padding: '8px 12px', borderBottom: idx < otherCases.length - 1 ? '1px solid #EEF0F5' : 'none', fontSize: 12 }}>
									<div style={{ fontWeight: 600, color: '#23282C' }}>
										{c.index_number || '--'} · {_formatStageLabel(c.case_stage)}
									</div>
									<div style={{ color: '#858AA1', fontSize: 11, marginTop: 2 }}>
										Filed {_formatDate(c.filing_date)}{c.original_amount ? ' · $' + _formatNumber(c.original_amount) : ''}
									</div>
								</div>
							))}
						</div>
					</div>
				)}

				{Array.isArray(liens) && liens.length > 0 && (
					<div style={sectionStyle}>
						<div style={labelStyle}>ACRIS Distress Liens ({liens.length})</div>
						<div style={{ marginTop: 8 }}>
							{liens.map((l, idx) => (
								<div key={l.id || idx} style={{ padding: '8px 0', borderBottom: idx < liens.length - 1 ? '1px solid #EEF0F5' : 'none', fontSize: 12 }}>
									<div style={{ fontWeight: 600, color: '#23282C' }}>
										{ACRIS_DOC_LABELS[l.doc_type] || (l.doc_type ? l.doc_type.toUpperCase() : 'Lien')}
										{l.document_amt ? ' · $' + _formatNumber(l.document_amt) : ''}
									</div>
									<div style={{ color: '#858AA1', fontSize: 11, marginTop: 2 }}>
										Recorded {_formatDate(l.recorded_datetime)}
										{l.party1 ? ' · ' + _capitalizeText(l.party1) : ''}
									</div>
								</div>
							))}
						</div>
					</div>
				)}

				{fc && fc.last_checked_at && (
					<div style={{ paddingTop: 12, fontSize: 11, color: '#858AA1' }}>
						Source: NYSCEF • Last checked {moment(fc.last_checked_at).fromNow()}
					</div>
				)}
			</div>

			<ButtonToolbar className="modal__footer offering-status">
				<hr />
				{fc && fc.case_url && (
					<Button
						className="btn btn-outline-primary"
						color="primary"
						variant="outlined"
						onClick={() => window.open(fc.case_url, '_blank')}
						style={{ marginRight: 8 }}
					>
						Open in NYSCEF <OpenInNewIcon size={14} style={{ marginLeft: 4 }} />
					</Button>
				)}
				<Button className="modal_ok btn btn-primary" onClick={onClose} color="primary">
					Close
				</Button>
			</ButtonToolbar>
		</Modal>
	);
};

export default ForeclosureDetailModal;
